/**
 * The app chrome: dark top bar, and navigation that differs by screen size.
 *
 * On a phone the top bar carries only the school name and the account button —
 * the menu moves to a bottom bar (see ./MobileNav). Measured before that split:
 * at 375px the document was 625px wide, the nav ran off the right edge and two
 * whole groups were unreachable. This ships as an installed PWA, so that was
 * the common case being broken, not an edge case.
 *
 * Both navigations read the SAME filtered list, so a phone can never offer a
 * destination the desktop hides, or vice versa.
 */
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthProvider";
import { selectedSchool } from "@/features/auth/session";
import { ROUTES } from "./routes";
import { Nav } from "./Nav";
import { bottomTiles, MobileNav } from "./MobileNav";
import { NavDrawer } from "./NavDrawer";
import { AccountMenu } from "./AccountMenu";
import { useDrainOnReconnect } from "@/offline/useOffline";

export function AppShell({ children }: { children: ReactNode }) {
  const { user, canAny, signOut, offline } = useAuth();
  // Mounted once, here: recovers rows a killed tab left in flight, then drains
  // whenever connectivity comes back.
  useDrainOnReconnect();
  const school = selectedSchool();
  const navigate = useNavigate();
  const [logoFailed, setLogoFailed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const items = ROUTES
    // Same gate as the router in App.tsx — the menu must never advertise a
    // route the router will not mount.
    .filter((route) => route.nav
      && (!route.perms?.length || canAny(route.perms))
      && (!route.roles?.length || route.roles.includes(user?.role ?? "")))
    .sort((a, b) => a.nav!.order - b.nav!.order);

  const initials = (user?.full_name ?? "?")
    .split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");

  // Everything the phone's bottom bar could not fit. Without this a parent —
  // whose only destinations are Notifications and Paramètres — had no
  // navigation at all on a phone, and a director could not reach Paramètres.
  // The routes that earn a place in the bar and on the phone's bottom bar.
  const primary = bottomTiles(items);
  const tiles = new Set(primary.map((route) => route.path));
  // Everything the phone's bottom bar could not fit, for the account menu.
  // The ☰ drawer lists EVERYTHING, primary tabs included, so a section is
  // never missing from the one place that claims to be complete.
  const overflow = items.filter((route) => !tiles.has(route.path));

  return (
    <div className="min-h-screen bg-bg">
      <header className="bg-chrome text-white">
        <div className="mx-auto flex h-[52px] max-w-wide items-center gap-6 px-4">
          {/* ☰ with the word beside it. The bar carries only the primary
              tabs, so every other section lives in here — an unlabelled
              hamburger does not say that, and the sections stay unfound. */}
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
            className="flex h-[38px] shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-white"
          >
            <span aria-hidden className="text-xl leading-none">☰</span>
            <span className="text-sm font-semibold">Menu</span>
          </button>

          {/* The school, as a link home — logo when the school has one, name
              either way.

              The name comes from the SESSION first and the picker second. They
              are different facts: the picker records what you chose on the
              login screen and lives in sessionStorage, so clearing site data
              or opening the installed PWA on another device left an
              authenticated user looking at the generic "Dalan" above their own
              school's figures. /api/v1/me has carried organization_name all
              along.

              The logo is auth-gated and same-origin, so a plain <img> carries
              the session cookie; a school without one 404s and the image
              removes itself, leaving the name. */}
          <button
            type="button"
            aria-label="Accueil"
            onClick={() => navigate("/")}
            className="flex min-w-0 flex-1 items-center gap-2 truncate text-left font-bold md:flex-none"
          >
            {user?.organization_id && !logoFailed && (
              <img
                src={`/api/v1/orgs/${encodeURIComponent(user.organization_id)}/branding/logo`}
                alt=""
                aria-hidden
                onError={() => setLogoFailed(true)}
                className="h-[30px] w-auto max-w-[120px] shrink-0 rounded object-contain"
              />
            )}
            <span className="truncate">
              {user?.organization_name ?? school?.name ?? "Dalan"}
            </span>
          </button>
          {/* Desktop only — the phone gets the bottom bar instead. Primary
              tabs are exactly the phone's tiles, so the two navigations can
              never disagree about what is primary. */}
          <div className="ml-auto hidden min-w-0 md:flex">
            <Nav items={primary} />
          </div>
          <AccountMenu
            initials={initials}
            overflow={overflow}
            onSignOut={() => void signOut()}
          />
        </div>
      </header>

      {offline && (
        // Says it once, at the top, for the whole app: everything below was
        // read from this device, not from the school's server. Without it a
        // restored session is indistinguishable from a live one — and someone
        // would act on a balance that has since changed.
        <div className="bg-warning px-4 py-1.5 text-center text-sm font-medium text-white">
          Hors ligne — données enregistrées sur cet appareil. Vos saisies
          partiront dès le retour du réseau.
        </div>
      )}

      {/* pb-20 clears the fixed bottom bar so the last row of a table is not
          sitting underneath it. Dropped at md, where that bar is gone. */}
      <main className="mx-auto max-w-wide p-4 pb-20 md:pb-4">{children}</main>

      <MobileNav items={items} />

      <NavDrawer items={items} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}
